import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Radio,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axios from 'axios';
import {
  Search,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { getToken } from '../../services/authAPIS';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { exportAsPDF, exportAsExcel, exportAsCSV } from '../../utils/importExport';
import { showSnackbar } from '../../components/Snackbar';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const CommissionReport = () => {
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });


  // More Filters States
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [tempDateFilter, setTempDateFilter] = useState(null);
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setDateFilter(tempDateFilter);
    setActiveFilters(prev => ({
      ...prev,
      fromDate: tempFromDate,
      toDate: tempToDate,
    }));
    setPage(0);
    setMoreFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setDateFilter(null);
    setTempDateFilter(null);
    setTempFromDate('');
    setTempToDate('');
    setActiveFilters(prev => ({
      ...prev,
      fromDate: '',
      toDate: '',
    }));
    setPage(0);
  };

  // Filter Input State
  const [filterInputs, setFilterInputs] = useState({
    associate: '', 
  });

  // Active Filter State (Sent to API)
  const [activeFilters, setActiveFilters] = useState({
    associate: '', 
    fromDate: '',
    toDate: '',
  });

  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  
  // Data State
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState('');

  // 1. Load Associates for Dropdown
  useEffect(() => {
    let cancelled = false;
    const loadAssociates = async () => {
      setAssociatesLoading(true);
      try {
        const response = await getAssociates({ page: 1, limit: 10 });
        const list = Array.isArray(response?.data) ? response.data : [];
        if (!cancelled) setAssociates(list);
      } catch (error) {
        console.error('Failed to fetch associates', error);
      } finally {
        if (!cancelled) setAssociatesLoading(false);
      }
    };
    loadAssociates();
    return () => { cancelled = true; };
  }, []);

  // 2. Fetch Report Data
  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setCommissionsLoading(true);
      setCommissionsError('');
      
      try {
        const params = new URLSearchParams();
        params.append('page', page + 1);
        params.append('limit', rowsPerPage);

        if (activeFilters.associate) params.append('associateId', activeFilters.associate);
        if (activeFilters.fromDate) params.append('fromDate', activeFilters.fromDate);
        if (activeFilters.toDate) params.append('toDate', activeFilters.toDate);
        if (dateFilter) params.append('date', dateFilter);

        if (sortBy) {
          let apiSortBy = '';
          let apiSortOrder = 'desc';

          if (sortBy === 'commissionPercentDesc') {
            apiSortBy = 'commissionPercent';
            apiSortOrder = 'desc';
          } else if (sortBy === 'commissionPercentAsc') {
            apiSortBy = 'commissionPercent';
            apiSortOrder = 'asc';
          } else if (sortBy === 'commissionAmountDesc') {
            apiSortBy = 'commissionAmount';
            apiSortOrder = 'desc';
          } else if (sortBy === 'commissionAmountAsc') {
            apiSortBy = 'commissionAmount';
            apiSortOrder = 'asc';
          } else if (sortBy === 'dueAmountDesc') {
            apiSortBy = 'dueAmount';
            apiSortOrder = 'desc';
          } else if (sortBy === 'dueAmountAsc') {
            apiSortBy = 'dueAmount';
            apiSortOrder = 'asc';
          } else if (sortBy === 'paidAmountDesc') {
            apiSortBy = 'paidAmount';
            apiSortOrder = 'desc';
          } else if (sortBy === 'paidAmountAsc') {
            apiSortBy = 'paidAmount';
            apiSortOrder = 'asc';
          }

          if (apiSortBy) {
            params.append('sortBy', apiSortBy);
            params.append('sortOrder', apiSortOrder);
          }
        }

        const token = getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/associate-commissions/commissionReport`,
          {
            params,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (cancelled) return;

        const rawData = res?.data?.data || [];
        const totalRecords = res?.data?.total || res?.data?.count || 0;
        const list = Array.isArray(rawData) ? rawData : [];

        const normalized = list.map((item, idx) => {
          const paid = Number(item.paidAmount || 0);
          const due = Number(item.dueAmount || 0);
          const commissionAmt = Number(item.commissionAmount || 0);
          const status = due > 0 ? 'Pending' : 'Paid';

          return {
            sno: (page * rowsPerPage) + (idx + 1),
            associateId: item.associateId || '-',
            associateObjectId: item.associateObjectId || '',
            associateName: item.associateName || '-',
            commissionRate: Number(item.commissionPercent || 0),
            commissionAmount: commissionAmt,
            paidAmount: paid,
            dueAmount: due,
            status: status,
          };
        });

        setCommissions(normalized);
        setTotalCount(totalRecords);

      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to fetch commission report';
          setCommissionsError(msg);
        }
      } finally {
        if (!cancelled) setCommissionsLoading(false);
      }
    };

    fetchReport();

    return () => { cancelled = true; };
  }, [page, rowsPerPage, activeFilters, sortBy, dateFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeFilters.associate, activeFilters.fromDate, activeFilters.toDate, dateFilter]);

  // Handle Search Button Click
  const handleSearch = () => {
    // Reset page and update filters in a single batch to avoid race conditions
    setPage(0);
    setActiveFilters({ ...filterInputs });
  };

  const handleReset = () => {
    setFilterInputs({ associate: '' });
    setActiveFilters({
      associate: '',
      fromDate: '',
      toDate: '',
    });
    setSortBy('');
    setTempSortBy('');
    setDateFilter(null);
    setTempDateFilter(null);
    setTempFromDate('');
    setTempToDate('');
    setPage(0);
  };

  // Associate Autocomplete Helpers
  const associateOptions = useMemo(() => {
    const opts = (associates || []).map((assoc) => {
      const value = assoc?._id || '';
      const code = assoc?.associateId || '';
      const name = assoc?.name || 'Unnamed';
      return { 
        label: code ? `${name} (${code})` : name, 
        value,
        code,
        name, 
      };
    });
    return [{ label: 'All Associates', value: '', name: '' }, ...opts];
  }, [associates]);

  const associateFilterOptions = createFilterOptions({
    stringify: (option) => `${option.label} ${option.value}`,
  });

  // Build rows shared by all export formats
  const buildExportRows = (dataToExport) => {
    const dataSource = dataToExport || commissions;
    return dataSource.map(item => ({
      'S.No': item.sno,
      'Associate ID': item.associateId,
      'Associate Name': item.associateName,
      'Comm %': item.commissionRate,
      'Total Commission': item.commissionAmount,
      'Paid Amount': item.paidAmount,
      'Due Amount': item.dueAmount,
      'Status': item.status,
    }));
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(activeFilters.fromDate || '');
    setExportToDate(activeFilters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('export', 'true');

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (activeFilters.associate) params.append('associateId', activeFilters.associate);

        // Date range based on selected mode
        if (exportDateMode === 'current') {
          if (activeFilters.fromDate) params.append('fromDate', activeFilters.fromDate);
          if (activeFilters.toDate) params.append('toDate', activeFilters.toDate);
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.append('fromDate', exportFromDate);
          if (exportToDate) params.append('toDate', exportToDate);
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.append('fromDate', exportFromDate);
            params.append('toDate', exportFromDate);
          }
        }
      }

      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/associate-commissions/commissionReport`,
        {
          params,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      const rawData = res?.data?.data || [];
      const list = Array.isArray(rawData) ? rawData : [];

      if (list.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const normalized = list.map((item, idx) => {
        const paid = Number(item.paidAmount || 0);
        const due = Number(item.dueAmount || 0);
        const commissionAmt = Number(item.commissionAmount || 0);
        const status = due > 0 ? 'Pending' : 'Paid';

        return {
          sno: idx + 1,
          associateId: item.associateId || '-',
          associateName: item.associateName || '-',
          commissionRate: Number(item.commissionPercent || 0),
          commissionAmount: commissionAmt,
          paidAmount: paid,
          dueAmount: due,
          status: status,
        };
      });

      const rows = buildExportRows(normalized);
      const fileName = 'commission_report';

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        exportAsPDF({ rows, title: 'Commission Report', fileNameBase: fileName });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Commission Report', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>Commission Report</Typography>
          <Typography variant="body2" color="text.secondary">Track commission payments and dues</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={() => handleOpenExportDialog('pdf')}
            onExportCsv={() => handleOpenExportDialog('csv')}
            onExportExcel={() => handleOpenExportDialog('excel')}
            variant="contained"
          />
          <RefreshButton
            onClick={() => setActiveFilters({ ...activeFilters })}
            loading={commissionsLoading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Associate Search */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={associateOptions}
                filterOptions={associateFilterOptions}
                value={associateOptions.find(o => o.value === filterInputs.associate) || associateOptions[0]}
                onChange={(_, newValue) => {
                  const selectedVal = newValue?.value || '';
                  setFilterInputs(prev => ({ ...prev, associate: selectedVal }));
                  setActiveFilters(prev => ({ ...prev, associate: selectedVal }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Search Associate" 
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {associatesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* SEARCH & RESET BUTTONS */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  fullWidth
                  variant="contained" 
                  onClick={handleSearch}
                  startIcon={<Search />}
                  disabled={commissionsLoading}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  {commissionsLoading ? 'Searching...' : 'Search'}
                </Button>
                <Button 
                  fullWidth
                  variant="outlined" 
                  onClick={handleReset}
                  disabled={commissionsLoading}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>

            {/* MORE FILTERS */}
            <Grid item xs={12} md={3}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                sortBy={tempSortBy}
                onChangeSortBy={setTempSortBy}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                type="commissionReport"
                height="40px"
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search by Single Day"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempDateFilter || ''}
                      onChange={(e) => {
                        const val = e.target.value || null;
                        setTempDateFilter(val);
                        if (val) {
                          setTempFromDate('');
                          setTempToDate('');
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Receive From"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempFromDate || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTempFromDate(val);
                        if (val) {
                          setTempDateFilter(null);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Receive To"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={tempToDate || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTempToDate(val);
                        if (val) {
                          setTempDateFilter(null);
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer
        component={Paper}
        key={`table-${activeFilters.associate}-${activeFilters.fromDate}-${activeFilters.toDate}`}
        sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: '#536DFE' }}>
            <TableRow>
              {["S.No", "Associate ID", "Associate Name", "Comm %", "Total Comm", "Paid", "Due", "Status"].map((head) => (
                <TableCell key={head} sx={{ color: '#fff', fontWeight: 700 }}>{head}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {commissionsError ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'error.main' }}>{commissionsError}</TableCell></TableRow>
            ) : commissionsLoading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : commissions.length > 0 ? (
              commissions.map((row) => (
                <TableRow key={row.associateObjectId + row.sno} hover>
                  <TableCell>{row.sno}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.associateId}</TableCell>
                  <TableCell>{row.associateName}</TableCell>
                  <TableCell>{row.commissionRate}%</TableCell>
                  <TableCell sx={{ color: '#1976d2', fontWeight: 700 }}>Rs. {Number(row.commissionAmount).toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ color: '#2e7d32' }}>Rs. {Number(row.paidAmount).toLocaleString('en-IN')}</TableCell>
                  <TableCell sx={{ color: '#d32f2f' }}>Rs. {Number(row.dueAmount).toLocaleString('en-IN')}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status} 
                      size="small" 
                      color={row.status === 'Paid' ? 'success' : 'warning'} 
                      variant={row.status === 'Paid' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          
          component="div"
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={commissionsLoading}
        />
      </TableContainer>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Commission Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {activeFilters.fromDate && activeFilters.toDate
                    ? `${activeFilters.fromDate} → ${activeFilters.toDate}`
                    : activeFilters.fromDate ? `From ${activeFilters.fromDate}` : activeFilters.toDate ? `Till ${activeFilters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default CommissionReport;